import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../api';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    } else {
      setSettings(null);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/v2/admin/me/settings');
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setError('Failed to load settings');
      setSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      setError('');
      const response = await api.put('/v2/admin/me/settings', newSettings);
      if (response.data.success) {
        // Merge instead of replace — the PUT response may not include every field
        setSettings(prev => ({ ...prev, ...response.data.settings }));
        return { success: true, settings: response.data.settings };
      }
      return { success: false, message: response.data.message || 'Failed to update settings' };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update settings';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const clearError = () => {
    setError('');
  };

  const value = {
    settings,
    loading,
    error,
    fetchSettings,
    updateSettings,
    clearError,
    hasSettings: !!settings,
    hasWebhook: settings?.webhookUrl && settings.webhookUrl.startsWith('https://'),
    hasTTS: settings?.elevenLabsVoiceId && settings?.elevenLabsApiKeyEncrypted,
    hasSTT: !!settings?.sttLang
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
